import { LoroText } from "loro-crdt";
import { useEffect, useRef, useState } from "react";
import { mountCodeMirror } from "../../editors/CodeMirrorView";
import { useLoroSelector } from "../../sync/hooks";
import type { BlockComponentProps } from "../registry";
import { bodyMap, ensureText } from "../textField";
import { useT } from "../../i18n";

let mermaidPromise: Promise<typeof import("mermaid").default> | null = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then((m) => {
      // spec §8.8: strict security level; no auto start
      m.default.initialize({ startOnLoad: false, securityLevel: "strict", theme: "neutral" });
      return m.default;
    });
  }
  return mermaidPromise;
}

let renderSeq = 0;

/** Mermaid block (spec §7.4.4, §8.8): source in CodeMirror, debounced render, inline errors. */
export function MermaidBlock({ handle, node, readOnly, onExitUp, onExitDown, onFocus }: BlockComponentProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const body = bodyMap(node);
  const source = useLoroSelector(handle.doc, () => {
    const t = body?.get("source");
    return t instanceof LoroText ? t.toString() : "";
  }) ?? "";
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showSource, setShowSource] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || !body) return;
    const text = ensureText(body, "source");
    if (!(body.get("source") instanceof LoroText)) handle.doc.commit({ origin: "sys:init" });
    const m = mountCodeMirror(el, { doc: handle.doc, text, readOnly, lineNumbers: false, onExitUp, onExitDown, onFocus });
    return () => m.view.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle.doc, node.id, readOnly, showSource]);

  useEffect(() => {
    if (!source.trim()) return;
    const t = setTimeout(async () => {
      try {
        const mermaid = await loadMermaid();
        const id = `mmd-${node.id.replace(/[^a-z0-9]/gi, "")}-${++renderSeq}`;
        const { svg } = await mermaid.render(id, source);
        setSvg(svg);
        setError(null);
      } catch (e) {
        // keep the last good render, show the parse error inline
        setError(e instanceof Error ? e.message : String(e));
        document.getElementById(`dmmd-${node.id.replace(/[^a-z0-9]/gi, "")}`)?.remove();
      }
    }, 300);
    return () => clearTimeout(t);
  }, [source, node.id]);

  return (
    <div className="rounded-md border border-neutral-200 bg-[var(--surface)] shadow-[var(--shadow-sm)] dark:border-neutral-800" data-testid="mermaid-block" data-fill-column>
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-[var(--surface-2)] px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em] text-neutral-500 dark:border-neutral-800">
        <span>mermaid</span>
        <button className="underline" onClick={() => setShowSource((s) => !s)}>
          {showSource ? t("mermaid.hideSource") : t("mermaid.showSource")}
        </button>
        {error && source.trim() && <span className="text-red-600" data-testid="mermaid-error">{error.split("\n")[0]}</span>}
      </div>
      <div className="grid gap-2 md:grid-cols-2" data-fill-surface>
        {showSource && <div ref={ref} className="min-w-0" />}
        {/* mermaid.render returns sanitised SVG under securityLevel "strict" */}
        <div className="min-w-0 overflow-auto p-2 [&_svg]:max-w-full" data-testid="mermaid-preview" data-fill-drawing dangerouslySetInnerHTML={{ __html: source.trim() ? svg : "" }} />
      </div>
    </div>
  );
}
